import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import Loader from "../../components/loader/Loader";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { getMasterConfig } from "wallet-elementkit/dist/constants/master";
import Color from "../../constants/Color";

import "../../styles/components/baseButton.scss";
import BaseIcon from "../baseIcon/BaseIcon";

const StyledButton = styled.div`
	.customizable-icon {
		fill: ${Color.mainBlue} !important;
		stroke: ${Color.mainBlue} !important;
	}
	.loader {
		z-index: 99;
		position: absolute;
		top: 0;
		left: ${(p) => p.width * 0.5 - p.height * 0.5}px;
	}

	.primary,
	.secondary,
	.tertiary,
	.success,
	.danger {
		${(p) =>
			p.hoverDisabled
				? `
        &::after, &::before {
            content: none !important;
        }
        `
				: `
                &::after {
            width: ${p.width}px !important;
            height: ${p.width}px !important;
            transform: translate(
                    ${`-${p.width / 2}px, -${p.width / 2}px`}
                )
                scale(0.1);
        }
        &:hover::after {
            transform-origin: center center !important;
            transform: scale(1)
                translate(${`-${p.width / 2}px, -${p.width / 2}px`}) !important;
        }`}
	}
	${(p) =>
		p.primaryColor && p.primaryColor !== Color.mainBlue
			? `
		.customizable-icon {
			fill: ${p.primaryColor} !important;
			stroke: ${p.primaryColor} !important;
		}
		.primary:not(.branding-disabled) {
			background-color: ${p.primaryColor};
			&::after {
				background-color: rgba(255,255,255,0.5);
			}
		}
		.secondary:not(.branding-disabled) {
			border-color: ${p.primaryColor};
			color: ${p.primaryColor};
			p,
			span,
			a {
				color: ${p.primaryColor};
			}
			svg {
				color: ${p.primaryColor};
			}
			&::after {
				background-color: ${p.hoverColor};
			}
		}`
			: ``}
`;
const masterConfig = getMasterConfig();

const getHoverColor = (color) => {
	let rgb = color.charAt(0) === "#" ? hexToRgb(color) : color;
	const rgbArray = rgb.split("(")[1].split(")")[0].split(",");
	const rgba = `rgba(${rgbArray[0]},${rgbArray[1]},${rgbArray[2]},0.2)`;
	return rgba;
};

const hexToRgb = (hex) => {
	var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	const r = parseInt(result[1], 16);
	const g = parseInt(result[2], 16);
	const b = parseInt(result[3], 16);
	return result ? `rgb(${r},${g},${b})` : null;
};

function Button(props) {
	const [isHover, setIsHover] = useState(false);
	const [width, setWidth] = useState(0);
	const [innerWidth, setInnerWidth] = useState(0);
	const [height, setHeight] = useState(0);

	const buttonRef = useRef(null);
	const buttonInnerRef = useRef(null);
	const id = useMemo(() => uuidv4(), []);

	const triggerOnCLick = () => {
		if (props.onClickAction && !props.disabled) {
			props.onClickAction();
		}
	};

	const getTextStyle = () => {
		switch (props.size) {
			case "large":
				return "s5";
			case "small":
				return "s7";
			default:
				return "s6";
		}
	};

	useLayoutEffect(() => {
		if (buttonRef.current) {
			setWidth(buttonRef.current.offsetWidth);
			setHeight(buttonRef.current.offsetHeight);
		}
		if (buttonInnerRef.current) {
			setInnerWidth(buttonInnerRef.current.offsetWidth);
		}
	}, [buttonRef.current, buttonInnerRef.current, id]);

	return (
		<StyledButton
			width={width}
			height={height}
			hoverDisabled={props.hoverDisabled ? true : false}
			primaryColor={props.backgroundColor || masterConfig.primaryColor}
			hoverColor={getHoverColor(
				props.backgroundColor || masterConfig.primaryColor
			)}
		>
			<div ref={props.ref} className="button-wrapper">
				{props.loading && (
					<Loader
						type={props.type === "primary" ? "secondary" : "primary"}
						height={height}
						width={width}
						className="loader"
						styles={{
							...props.loadingSpinnerStyles,
						}}
					/>
				)}
				<div
					onClick={triggerOnCLick}
					ref={buttonRef}
					onMouseEnter={() => {
						setIsHover(true);
					}}
					onMouseLeave={() => {
						setIsHover(false);
					}}
					className={`base-button flex-row jc-c ${props.type} ${props.size} ${
						props.className ? props.className : ""
					} ${getTextStyle()} ${id}
         ${props.disabled ? "disabled" : ""}`}
					style={{
						width:
							props.width && innerWidth > props.width
								? `fit-content`
								: `${props.width}px`,
						minWidth:
							props.width && innerWidth > props.width
								? `fit-content`
								: `${props.width}px`,
						maxWidth:
							props.width && innerWidth > props.width
								? `fit-content`
								: `${props.width}px`,
						minHeight: `${props.height}px`,
						maxHeight: `${props.height}px`,
						opacity: props.loading ? "0.4" : "1",
						...props.styles,
					}}
				>
					<div ref={buttonInnerRef} className="button-inner">
						{props.prefixIcon && (
							<BaseIcon
								icon={props.prefixIcon}
								styles={{
									marginRight: "10px",
								}}
							/>
						)}
						{props.children}
						{props.suffixIcon && (
							<BaseIcon
								icon={props.suffixIcon}
								styles={{
									marginLeft: "10px",
								}}
							/>
						)}
					</div>
				</div>
			</div>
		</StyledButton>
	);
}

export default Button;
